import {
  CHART_CALCULATION,
  CHART_FIELD,
  type CHART_FREQUENCY,
  CHART_GRAPH,
  CHART_METRIC,
  CHART_METRIC_VIEW_TYPE,
  CHART_PERIOD_RELATIVE,
  CHART_SHARE_CALCULATION_TYPE,
  CHART_TERM_TYPE,
  CHART_TERM_UNIT,
  CHART_TYPE,
  ENTIRE_ONLY_FIELDS,
} from '@/entities/chart/model/constants';
import {
  getChartMetricPolicy,
} from '@/features/(charts)/chart-metric-policy';
import chartUtils from '@/features/(charts)/chart-renderer/lib/chart-utils';
import { isCalculationEnabled } from '@/features/(charts)/chart-renderer/lib/conditions';
import { FeedbackSentiment } from '@/types/feedbackConst';
import { getTimezoneDayjs } from '@/utils/time';

/**
 * Creates a default cross-axis series
 */
export function createDefaultCrossAxisSeries(): ChartSeriesDef {
  return {
    cross_field: {
      type: CHART_FIELD.FEEDBACK_ALL,
      candidates: [],
      entire: true,
    },
    measure: {
      metric: CHART_METRIC.FEEDBACK,
      calculation: CHART_CALCULATION.COUNT,
    },
    options: {
      view_type: CHART_METRIC_VIEW_TYPE.COUNT,
    },
  };
}

/**
 * Creates a default single-axis series
 */
export function createDefaultSingleAxisSeries(
  existingSeries?: ChartSeriesDef,
): ChartSeriesDef {
  return {
    measure: existingSeries?.measure || {
      metric: CHART_METRIC.FEEDBACK,
      calculation: CHART_CALCULATION.COUNT,
    },
    options: existingSeries?.options,
  };
}

/**
 * Creates series for combo chart
 */
export function createComboChartSeries(
  currentSeries: ChartSeriesDef[],
): ChartSeriesDef[] {
  const { comboSecondary } = getChartMetricPolicy();

  if (currentSeries.length >= 2) {
    return currentSeries.slice(0, 2);
  } else if (currentSeries.length === 1) {
    return [
      ...currentSeries,
      {
        measure: {
          metric: comboSecondary.metric,
          calculation: comboSecondary.calculation,
        },
      },
    ];
  } else {
    return [
      {
        measure: {
          metric: CHART_METRIC.FEEDBACK,
          calculation: CHART_CALCULATION.COUNT,
        },
      },
      {
        measure: {
          metric: comboSecondary.metric,
          calculation: comboSecondary.calculation,
        },
      },
    ];
  }
}

/**
 * Updates series measure
 */
export function updateSeriesMeasure(
  series: ChartSeriesDef,
  metric: CHART_METRIC,
  currentMetric: CHART_METRIC,
): ChartSeriesDef {
  if (isCalculationEnabled(metric)) {
    const shouldResetCellShareCalculation =
      metric === CHART_METRIC.IMPRESSION &&
      series.options?.share_calculation_type ===
        CHART_SHARE_CALCULATION_TYPE.CELL;
    const options = series.options
      ? {
          ...series.options,
          view_type: CHART_METRIC_VIEW_TYPE.COUNT,
          share_calculation_type: shouldResetCellShareCalculation
            ? CHART_SHARE_CALCULATION_TYPE.GRAND_TOTAL
            : series.options.share_calculation_type,
          share_cell_calculation_segment: shouldResetCellShareCalculation
            ? undefined
            : series.options.share_cell_calculation_segment,
        }
      : undefined;
    return {
      ...series,
      measure: {
        ...series.measure,
        metric,
        calculation: isCalculationEnabled(currentMetric)
          ? series.measure.calculation
          : CHART_CALCULATION.AVERAGE,
      },
      options,
    };
  } else {
    const options = {
      ...series.options,
      share_calculation_type: CHART_SHARE_CALCULATION_TYPE.GRAND_TOTAL,
      share_cell_calculation_segment: FeedbackSentiment.NEGATIVE,
    };

    return {
      ...series,
      measure: {
        ...series.measure,
        metric,
        calculation: CHART_CALCULATION.COUNT,
      },
      options,
    };
  }
}

/**
 * Updates series options
 */
export function updateSeriesOptions(
  series: ChartSeriesDef,
  options: Partial<ChartOptions>,
): ChartSeriesDef {
  return {
    ...series,
    options: {
      ...series.options,
      ...options,
    },
  };
}

/**
 * Creates term definition for FIXED type
 */
export function createFixedTerm(
  currentUnit: CHART_TERM_UNIT,
  historicalFrequency: CHART_FREQUENCY | null,
): ChartFixedTermDef {
  const startDate = getTimezoneDayjs().startOf('month').toJSON();
  const endDate = getTimezoneDayjs()
    .addVal(1, 'month')
    .startOf('month')
    .toJSON();

  return {
    type: CHART_TERM_TYPE.FIXED,
    unit: historicalFrequency
      ? chartUtils.getUnitByFrequency(historicalFrequency)
      : currentUnit,
    start_date: startDate,
    end_date: endDate,
  };
}

/**
 * Creates term definition for PERIODIC type
 */
export function createPeriodicTerm(): ChartPeriodicTermDef {
  return {
    value: 3,
    unit: CHART_TERM_UNIT.DAY,
    relative: CHART_PERIOD_RELATIVE.INCLUDE,
    type: CHART_TERM_TYPE.PERIODIC,
  };
}

/**
 * Gets view type options based on view type selection
 */
export function getViewTypeOptions(
  viewType: CHART_METRIC_VIEW_TYPE,
  currentOptions?: ChartOptions,
): ChartOptions {
  switch (viewType) {
    case CHART_METRIC_VIEW_TYPE.COUNT:
      return {
        ...currentOptions,
        view_type: viewType,
      };
    case CHART_METRIC_VIEW_TYPE.PERCENTAGE:
      return {
        ...currentOptions,
        view_type: viewType,
        share_calculation_type: CHART_SHARE_CALCULATION_TYPE.GRAND_TOTAL,
      };
  }
}

/**
 * Gets share calculation options
 */
export function getShareCalculationOptions(
  shareCalculationType: CHART_SHARE_CALCULATION_TYPE,
  currentOptions?: ChartOptions,
): ChartOptions {
  return {
    ...currentOptions,
    share_calculation_type: shareCalculationType,
    share_cell_calculation_segment:
      shareCalculationType === CHART_SHARE_CALCULATION_TYPE.CELL
        ? FeedbackSentiment.NEGATIVE
        : undefined,
  };
}

/**
 * Transforms field to cross_field and vice versa for dataset switch
 */
export function swapFieldAndCrossField(
  field: ChartFieldDef,
  series: ChartSeriesDef[],
): {
  newField: ChartFieldDef;
  newSeries: ChartSeriesDef[];
} {
  if (series.length > 1) {
    return { newField: field, newSeries: series };
  }

  const newField = series[0]?.cross_field
    ? {
        ...series[0].cross_field,
        entire: ENTIRE_ONLY_FIELDS.has(series[0].cross_field.type),
      }
    : {
        type: CHART_FIELD.FEEDBACK_TAG,
        candidates: [],
        entire: false,
      };

  const newSeries = series.map((s, index) =>
    index === 0
      ? {
          ...s,
          cross_field: {
            type: field.type,
            candidates: field.candidates,
            entire: ENTIRE_ONLY_FIELDS.has(field.type),
          },
        }
      : s,
  );

  return { newField, newSeries };
}

/**
 * Checks if a chart type allows historical data
 */
export function shouldAllowHistorical(
  chartType: CHART_TYPE,
  graph: CHART_GRAPH,
): boolean {
  // CROSS_AXIS doesn't allow historical
  if (chartType === CHART_TYPE.CROSS_AXIS) {
    return false;
  }

  // TABLE doesn't allow historical
  if (graph === CHART_GRAPH.TABLE) {
    return false;
  }

  return true;
}

/**
 * Checks if graph allows multiple series
 */
export function allowsMultipleSeries(
  chartType: CHART_TYPE,
  graph: CHART_GRAPH,
): boolean {
  return (
    (chartType === CHART_TYPE.SINGLE_AXIS &&
      graph === CHART_GRAPH.COMBO_CHART) ||
    (chartType === CHART_TYPE.CROSS_AXIS && graph === CHART_GRAPH.TABLE)
  );
}
